import React from 'react';
import { motion } from 'framer-motion';
import { FiCheck, FiLayers, FiDroplet, FiTruck, FiArrowRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { Link } from 'react-router-dom';

const services = [
  {
    icon: FiLayers,
    tag: 'Most Popular',
    tagColor: 'bg-red-600',
    title: 'Residential Garage Systems',
    subtitle: 'The ultimate garage upgrade',
    desc: 'Our signature multi-layer flake and polyaspartic systems transform ordinary concrete into an extraordinary, showroom-quality floor. Completed in as little as one day with minimal disruption.',
    img: 'https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/b57b6fc9c4fda80eb5277417408644ea.jpg',
    features: [
      '100% UV-Stable Polyaspartic Topcoat',
      'Hot-tire & road-salt resistant',
      'Slip-resistant texture options',
      'Dozens of custom color blends',
      'Typically 1-day installation'
    ],
    cta: 'Get a Garage Quote',
    highlight: false
  },
  {
    icon: FiTruck,
    tag: 'Commercial Grade',
    tagColor: 'bg-blue-700',
    title: 'Commercial & Industrial Warehouses',
    subtitle: 'Built for the toughest environments',
    desc: 'High-build epoxy and urethane systems engineered for distribution centers, manufacturing plants, food processing facilities, and airplane hangars. Forklift-rated. Chemical resistant. OSHA compliant.',
    img: 'https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/15ce0561618377893538a047841f7c0a.jpeg',
    features: [
      'Forklift & heavy machinery rated',
      'Chemical & spill resistant',
      'Safety line striping & markings',
      'Anti-static & ESD options',
      'Large-scale rapid deployment'
    ],
    cta: 'Discuss Your Project',
    highlight: false
  },
  {
    icon: FiDroplet,
    tag: 'Premium Finish',
    tagColor: 'bg-red-800',
    title: 'Custom Metallic Epoxy',
    subtitle: 'Art you walk on',
    desc: 'For spaces that demand a truly unique, high-end aesthetic. Metallic pigments suspended in a crystal-clear resin create a brilliant, three-dimensional look resembling marble, molten metal, or flowing water.',
    img: 'https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/8a71fd759f539a6eb03c5a6723074eb5.jpg',
    features: [
      '100% unique, custom finish',
      'High-gloss, mirror-like surface',
      'Incredible visual depth & movement',
      'Perfect for showrooms & luxury garages',
      'UV stable — no yellowing'
    ],
    cta: 'Design Your Floor',
    highlight: true
  }
];

const Services = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="relative h-80 md:h-[500px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvcG5n/5c980b02fae98bba170d82c1e5aa61c5.png" 
            alt="Epoxy Services" 
            className="w-full h-full object-cover scale-105"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-red-900/80 via-black/50 to-black/80" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-red-400 text-sm font-black tracking-[0.2em] uppercase mb-4 block">What We Offer</span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white tracking-tight">Our Services</h1>
          </motion.div>
        </div>
      </div>

      {/* Services List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 space-y-16">
        {services.map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className={`rounded-[2.5rem] overflow-hidden shadow-xl border ${
              s.highlight ? 'bg-blue-50 border-blue-100' : 'bg-white border-gray-200'
            }`}
          >
            <div className={`grid md:grid-cols-2 ${i % 2 !== 0 ? 'md:[&>*:first-child]:order-2' : ''}`}>
              <div className="relative h-80 md:h-auto min-h-[500px] overflow-hidden">
                <img 
                  src={s.img} 
                  alt={s.title} 
                  className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                <div className="absolute top-8 left-8">
                  <span className={`${s.tagColor} text-white text-xs font-black px-4 py-2 rounded-full uppercase tracking-widest shadow-lg`}>
                    {s.tag}
                  </span>
                </div>
              </div>
              
              <div className="p-10 md:p-16 flex flex-col justify-center text-gray-900">
                <div className={`w-16 h-16 ${s.highlight ? 'bg-blue-100' : 'bg-red-50'} rounded-2xl flex items-center justify-center mb-8`}>
                  <SafeIcon icon={s.icon} className={`text-3xl ${s.highlight ? 'text-blue-600' : 'text-red-600'}`} />
                </div>
                
                <p className={`text-sm font-bold tracking-[0.2em] uppercase mb-3 ${s.highlight ? 'text-blue-600' : 'text-red-600'}`}>
                  {s.subtitle}
                </p>
                <h2 className="text-3xl md:text-5xl font-black mb-6 tracking-tight">{s.title}</h2>
                <p className="mb-10 leading-relaxed text-lg text-gray-600">{s.desc}</p>
                
                <ul className="space-y-4 mb-12">
                  {s.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-4 font-medium text-lg text-gray-700">
                      <div className={`w-6 h-6 rounded-full ${s.highlight ? 'bg-blue-100' : 'bg-red-100'} flex items-center justify-center flex-shrink-0`}>
                        <SafeIcon icon={FiCheck} className={`text-sm ${s.highlight ? 'text-blue-600' : 'text-red-600'}`} />
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>

                <Link 
                  to="/contact"
                  className={`inline-flex items-center gap-3 px-8 py-4 ${
                    s.highlight ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'
                  } text-white font-black rounded-xl transition-all shadow-lg self-start group text-lg`}
                >
                  {s.cta}
                  <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1.5 transition-transform" />
                </Link>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-32">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-red-700 via-red-800 to-blue-900 rounded-[3rem] p-12 md:p-20 text-center relative overflow-hidden shadow-2xl"
        >
          <div className="absolute inset-0 opacity-10 mix-blend-overlay">
            <img 
              src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvcG5n/11a0bc8500ea6364c3288db62640bb07.png" 
              className="w-full h-full object-cover"
              alt="Background"
              loading="lazy"
            />
          </div>
          
          <div className="relative z-10">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-tight">Not Sure Which System Is Right For You?</h2>
            <p className="text-red-100 text-xl md:text-2xl mb-12 max-w-2xl mx-auto font-light leading-relaxed">
              Our experts will visit your site, assess the concrete, and recommend the perfect system — absolutely free.
            </p>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-3 px-12 py-6 bg-white text-red-700 font-black rounded-full hover:bg-gray-100 transition-all shadow-xl text-lg group"
            >
              Book a Free Site Visit
              <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1.5 transition-transform" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Services;